<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ComboBoxDropdown from 'dashboard/components-next/combobox/ComboBoxDropdown.vue';
import { INBOX_TYPES, getReadableInboxByType } from 'dashboard/helper/inbox';
import {
  generateSummaryEntityFilterParams,
  parseSummaryEntityFilterParams,
} from '../helpers/reportFilterHelper';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value =>
      [
        'agent',
        'team',
        'label',
        'inbox',
        'account',
        'chats',
        'agent_activity',
        'queue',
        'bot',
      ].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideLabelFilter: {
    type: Boolean,
    default: false,
  },
  hideAgentFilter: {
    type: Boolean,
    default: false,
  },
  hideTeamFilter: {
    type: Boolean,
    default: false,
  },
  hideInboxFilter: {
    type: Boolean,
    default: false,
  },
  hideResolutionMethod: {
    type: Boolean,
    default: false,
  },
  syncUrl: {
    type: Boolean,
    default: true,
  },
  initialFilters: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const selectedAgentIds = ref([]);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const labelChipOrder = ref([]);
const labelDropdownOpen = ref(false);
const labelSearch = ref('');
const labelDropdownRef = ref(null);
const selectedChannelTypes = ref([]);
const episodeResult = ref('all');
const receiptType = ref('all');
const tagPresence = ref('all');
const resolutionMethod = ref('all');
const statusAtPeriodEnd = ref('all');
const reopening = ref('all');
const transfers = ref('all');
const humanResponse = ref('all');

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const toOptions = (items, labelKey = 'name') =>
  (items || []).map(item => ({
    value: item.id,
    label: item[labelKey] || item.title || item.name,
  }));

const agentOptions = computed(() => toOptions(agents.value));
const teamOptions = computed(() => toOptions(teams.value));
const inboxOptions = computed(() => toOptions(inboxes.value));
const labelOptions = computed(() => toOptions(labels.value, 'title'));
const availableLabelOptions = computed(() => {
  const active = new Set([...selectedLabelIds.value, ...excludeLabelIds.value]);
  return labelOptions.value.filter(option => !active.has(option.value));
});
const filteredLabelOptions = computed(() => {
  const searchTerm = labelSearch.value.toLowerCase();
  if (!searchTerm) return availableLabelOptions.value;
  return availableLabelOptions.value.filter(option =>
    option.label?.toLowerCase().includes(searchTerm)
  );
});
const labelFilterChips = computed(() =>
  labelChipOrder.value
    .filter(
      id =>
        selectedLabelIds.value.includes(id) ||
        excludeLabelIds.value.includes(id)
    )
    .map(id => {
      const option = labelOptions.value.find(item => item.value === id);
      return {
        id,
        name: option?.label || String(id),
        isExcluded: excludeLabelIds.value.includes(id),
      };
    })
);

const toggleLabelDropdown = () => {
  if (props.disabled) return;
  labelDropdownOpen.value = !labelDropdownOpen.value;
  if (labelDropdownOpen.value) {
    labelSearch.value = '';
    nextTick(() => labelDropdownRef.value?.focus());
  }
};

const addLabelFilter = option => {
  const id = option.value;
  if (
    selectedLabelIds.value.includes(id) ||
    excludeLabelIds.value.includes(id)
  ) {
    return;
  }
  selectedLabelIds.value = [...selectedLabelIds.value, id];
  if (!labelChipOrder.value.includes(id)) {
    labelChipOrder.value = [...labelChipOrder.value, id];
  }
};

const channelTypeOptions = computed(() =>
  Object.values(INBOX_TYPES).map(value => ({
    value,
    label: t(
      `SUMMARY_REPORTS.FILTERS.CHANNEL_TYPES.${getReadableInboxByType(value)}`
    ),
  }))
);

const enumOptions = (prefix, keys) =>
  keys.map(key => ({
    value: key,
    label: t(`${prefix}.${key.toUpperCase()}`),
  }));

const episodeResultOptions = computed(() =>
  enumOptions('SUMMARY_REPORTS.FILTERS.EPISODE_RESULT', [
    'all',
    'answered',
    'unanswered',
    'completed',
    'transferred',
  ])
);

const receiptTypeOptions = computed(() =>
  enumOptions('SUMMARY_REPORTS.FILTERS.RECEIPT_TYPE', [
    'all',
    'primary',
    'transfer',
  ])
);

const tagPresenceOptions = computed(() =>
  enumOptions('SUMMARY_REPORTS.FILTERS.TAG_PRESENCE', [
    'all',
    'with',
    'without',
  ])
);

const resolutionMethodOptions = computed(() =>
  enumOptions('SUMMARY_REPORTS.FILTERS.RESOLUTION_METHOD', [
    'all',
    'bot',
    'human',
  ])
);

const statusAtPeriodEndOptions = computed(() =>
  enumOptions('CHATS_REPORTS.FILTERS.STATUS_AT_PERIOD_END', [
    'all',
    'open',
    'pending',
    'snoozed',
    'resolved',
  ])
);

const reopeningOptions = computed(() =>
  enumOptions('CHATS_REPORTS.FILTERS.REOPENING', [
    'all',
    'only_reopened',
    'without_reopened',
  ])
);

const transfersOptions = computed(() =>
  enumOptions('CHATS_REPORTS.FILTERS.TRANSFERS', [
    'all',
    'without',
    'with_agent',
    'with_team',
  ])
);

const humanResponseOptions = computed(() =>
  enumOptions('CHATS_REPORTS.FILTERS.HUMAN_RESPONSE', [
    'all',
    'present',
    'absent',
  ])
);

const showAgents = computed(
  () =>
    !props.hideAgentFilter &&
    [
      'agent',
      'team',
      'label',
      'inbox',
      'account',
      'chats',
      'agent_activity',
      'queue',
    ].includes(props.type)
);
const showTeams = computed(
  () =>
    !props.hideTeamFilter &&
    [
      'agent',
      'team',
      'label',
      'inbox',
      'account',
      'chats',
      'agent_activity',
      'queue',
      'bot',
    ].includes(props.type)
);
const showInboxes = computed(
  () =>
    !props.hideInboxFilter &&
    [
      'agent',
      'team',
      'label',
      'inbox',
      'account',
      'chats',
      'queue',
      'bot',
    ].includes(props.type)
);
const showLabels = computed(
  () =>
    !props.hideLabelFilter &&
    [
      'agent',
      'team',
      'label',
      'inbox',
      'account',
      'chats',
      'queue',
      'bot',
    ].includes(props.type)
);
const showChannelTypes = computed(() => props.type === 'inbox');
const showEpisodeResult = computed(() => props.type === 'agent');
const showReceiptType = computed(() => props.type === 'team');
const showTagPresence = computed(() => props.type === 'label');
const showResolutionMethod = computed(
  () =>
    !props.hideResolutionMethod &&
    ['agent', 'team', 'label', 'inbox', 'account', 'chats'].includes(props.type)
);
const showChatsFilters = computed(() => props.type === 'chats');

const currentFilters = () => ({
  agentIds: selectedAgentIds.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  channelTypes: selectedChannelTypes.value,
  episodeResult: episodeResult.value,
  receiptType: receiptType.value,
  tagPresence: tagPresence.value,
  resolutionMethod: resolutionMethod.value,
  statusAtPeriodEnd: statusAtPeriodEnd.value,
  reopening: reopening.value,
  transfers: transfers.value,
  humanResponse: humanResponse.value,
});

const updateURLParams = () => {
  if (!props.syncUrl) return;

  const entityParams = generateSummaryEntityFilterParams(currentFilters());
  const nextQuery = { ...route.query, ...entityParams };

  [
    'agent_ids',
    'inbox_ids',
    'team_ids',
    'label_ids',
    'exclude_label_ids',
    'channel_types',
    'episode_result',
    'receipt_type',
    'tag_presence',
    'resolution_method',
    'status_at_period_end',
    'reopening',
    'transfers',
    'human_response',
  ].forEach(key => {
    if (!entityParams[key]) delete nextQuery[key];
  });

  router.replace({ query: nextQuery });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', currentFilters());
};

const syncLabelChipOrder = () => {
  const activeIds = [
    ...selectedLabelIds.value,
    ...excludeLabelIds.value.filter(id => !selectedLabelIds.value.includes(id)),
  ];
  labelChipOrder.value = [
    ...labelChipOrder.value.filter(id => activeIds.includes(id)),
    ...activeIds.filter(id => !labelChipOrder.value.includes(id)),
  ];
};

const toggleLabelFilterMode = id => {
  if (selectedLabelIds.value.includes(id)) {
    selectedLabelIds.value = selectedLabelIds.value.filter(
      value => value !== id
    );
    if (!excludeLabelIds.value.includes(id)) {
      excludeLabelIds.value = [...excludeLabelIds.value, id];
    }
  } else if (excludeLabelIds.value.includes(id)) {
    excludeLabelIds.value = excludeLabelIds.value.filter(value => value !== id);
    if (!selectedLabelIds.value.includes(id)) {
      selectedLabelIds.value = [...selectedLabelIds.value, id];
    }
  }
};

const removeLabelFilter = id => {
  selectedLabelIds.value = selectedLabelIds.value.filter(value => value !== id);
  excludeLabelIds.value = excludeLabelIds.value.filter(value => value !== id);
  labelChipOrder.value = labelChipOrder.value.filter(value => value !== id);
};

const applyFilters = filters => {
  const parsed = filters || {};
  selectedAgentIds.value = parsed.agentIds || [];
  selectedInboxIds.value = parsed.inboxIds || [];
  selectedTeamIds.value = parsed.teamIds || [];
  selectedLabelIds.value = parsed.labelIds || [];
  excludeLabelIds.value = parsed.excludeLabelIds || [];
  labelChipOrder.value = [
    ...selectedLabelIds.value,
    ...excludeLabelIds.value.filter(id => !selectedLabelIds.value.includes(id)),
  ];
  selectedChannelTypes.value = parsed.channelTypes || [];
  episodeResult.value = parsed.episodeResult || 'all';
  receiptType.value = parsed.receiptType || 'all';
  tagPresence.value = parsed.tagPresence || 'all';
  resolutionMethod.value = parsed.resolutionMethod || 'all';
  statusAtPeriodEnd.value = parsed.statusAtPeriodEnd || 'all';
  reopening.value = parsed.reopening || 'all';
  transfers.value = parsed.transfers || 'all';
  humanResponse.value = parsed.humanResponse || 'all';
};

const initializeFromURL = () => {
  applyFilters(parseSummaryEntityFilterParams(route.query));
};

const skipEntityFilterEmit = ref(true);

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  if (props.initialFilters) {
    applyFilters(props.initialFilters);
  } else {
    initializeFromURL();
  }
  await nextTick();
  skipEntityFilterEmit.value = false;
  if (props.initialFilters === null) {
    emitChange();
  }
});

watch(
  () => props.initialFilters,
  async next => {
    if (next === null || next === undefined) return;
    skipEntityFilterEmit.value = true;
    applyFilters(next);
    await nextTick();
    skipEntityFilterEmit.value = false;
  },
  { deep: true }
);

watch(
  [
    selectedAgentIds,
    selectedInboxIds,
    selectedTeamIds,
    selectedLabelIds,
    excludeLabelIds,
    selectedChannelTypes,
    episodeResult,
    receiptType,
    tagPresence,
    resolutionMethod,
    statusAtPeriodEnd,
    reopening,
    transfers,
    humanResponse,
  ],
  () => {
    if (skipEntityFilterEmit.value) return;
    emitChange();
  },
  { deep: true }
);

watch([selectedLabelIds, excludeLabelIds], syncLabelChipOrder, { deep: true });
</script>

<template>
  <div
    class="mt-4 flex w-full flex-col gap-2"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div class="grid w-full grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
      <TagMultiSelectComboBox
        v-if="showAgents"
        v-model="selectedAgentIds"
        :options="agentOptions"
        :placeholder="
          type === 'queue'
            ? $t('QUEUED_CUSTOMERS_REPORTS.FILTERS.ASSIGNED_OPERATOR')
            : $t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.AGENTS')
        "
        :search-placeholder="
          $t('AGENT_REPORTS.FILTERS.INPUT_PLACEHOLDER.AGENTS')
        "
      />
      <TagMultiSelectComboBox
        v-if="showTeams"
        v-model="selectedTeamIds"
        :options="teamOptions"
        :placeholder="
          type === 'bot'
            ? $t('BOT_REPORTS.FILTERS.TEAM_AFTER_HANDOFF')
            : $t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.TEAMS')
        "
        :search-placeholder="$t('TEAM_REPORTS.FILTERS.INPUT_PLACEHOLDER.TEAMS')"
      />
      <TagMultiSelectComboBox
        v-if="showInboxes"
        v-model="selectedInboxIds"
        :options="inboxOptions"
        :placeholder="$t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.INBOXES')"
        :search-placeholder="
          $t('INBOX_REPORTS.FILTERS.INPUT_PLACEHOLDER.INBOXES')
        "
      />
      <div v-if="showLabels" class="relative min-w-0">
        <OnClickOutside @trigger="labelDropdownOpen = false">
          <div
            class="flex min-h-[42px] w-full cursor-pointer flex-wrap items-center gap-2 rounded-lg border bg-n-alpha-black2 px-3 py-2.5 transition-all duration-500 ease-in-out"
            :class="
              labelDropdownOpen
                ? 'border-n-brand'
                : 'border-n-weak hover:border-n-slate-6'
            "
            @click="toggleLabelDropdown"
          >
            <div
              v-for="chip in labelFilterChips"
              :key="`label-${chip.id}`"
              class="inline-flex max-w-full items-center gap-1 rounded-full text-sm font-medium transition-colors"
              :class="[
                chip.isExcluded
                  ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                  : 'bg-n-alpha-black1 text-n-slate-12',
              ]"
              @click.stop
            >
              <button
                type="button"
                class="inline-flex min-w-0 items-center gap-1.5 rounded-l-full py-1 pl-2.5"
                :class="
                  chip.isExcluded
                    ? 'hover:bg-red-100 dark:hover:bg-red-900/50'
                    : 'hover:bg-n-alpha-2'
                "
                :title="
                  chip.isExcluded
                    ? $t('SUMMARY_REPORTS.FILTERS.LABELS.TOGGLE_TO_INCLUDE')
                    : $t('SUMMARY_REPORTS.FILTERS.LABELS.TOGGLE_TO_EXCLUDE')
                "
                @click="toggleLabelFilterMode(chip.id)"
              >
                <span
                  v-if="chip.isExcluded"
                  class="shrink-0 text-xs font-semibold opacity-80"
                >
                  {{ $t('SUMMARY_REPORTS.FILTERS.LABELS.EXCLUDE_PREFIX') }}
                </span>
                <span class="truncate">{{ chip.name }}</span>
              </button>
              <button
                type="button"
                class="inline-flex shrink-0 items-center rounded-r-full py-1 pr-2"
                :class="
                  chip.isExcluded
                    ? 'hover:bg-red-100 dark:hover:bg-red-900/50'
                    : 'hover:bg-n-alpha-2'
                "
                :aria-label="$t('SUMMARY_REPORTS.FILTERS.LABELS.REMOVE')"
                @click="removeLabelFilter(chip.id)"
              >
                <span class="i-lucide-x size-3.5 text-n-slate-11" />
              </button>
            </div>
            <span
              v-if="!labelFilterChips.length"
              class="text-sm text-n-slate-11"
            >
              {{ $t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.LABELS') }}
            </span>
          </div>
          <ComboBoxDropdown
            ref="labelDropdownRef"
            v-model:search-value="labelSearch"
            :open="labelDropdownOpen"
            :options="filteredLabelOptions"
            :search-placeholder="
              $t('LABEL_REPORTS.FILTERS.INPUT_PLACEHOLDER.LABELS')
            "
            multiple
            :selected-values="[]"
            @select="addLabelFilter"
          />
        </OnClickOutside>
        <p
          v-if="labelFilterChips.length"
          class="mt-1.5 text-xs text-n-slate-11"
        >
          {{ $t('SUMMARY_REPORTS.FILTERS.LABELS.CLICK_HINT') }}
        </p>
      </div>
      <TagMultiSelectComboBox
        v-if="showChannelTypes"
        v-model="selectedChannelTypes"
        :options="channelTypeOptions"
        :placeholder="$t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.CHANNEL_TYPES')"
        :search-placeholder="
          $t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.CHANNEL_TYPES')
        "
      />
      <ComboBox
        v-if="showEpisodeResult"
        v-model="episodeResult"
        :options="episodeResultOptions"
        :placeholder="$t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.EPISODE_RESULT')"
      />
      <ComboBox
        v-if="showReceiptType"
        v-model="receiptType"
        :options="receiptTypeOptions"
        :placeholder="$t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.RECEIPT_TYPE')"
      />
      <ComboBox
        v-if="showTagPresence"
        v-model="tagPresence"
        :options="tagPresenceOptions"
        :placeholder="$t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.TAG_PRESENCE')"
      />
      <ComboBox
        v-if="showResolutionMethod"
        v-model="resolutionMethod"
        :options="resolutionMethodOptions"
        :placeholder="
          $t('SUMMARY_REPORTS.FILTERS.PLACEHOLDER.RESOLUTION_METHOD')
        "
      />
      <ComboBox
        v-if="showChatsFilters"
        v-model="statusAtPeriodEnd"
        :options="statusAtPeriodEndOptions"
        :placeholder="
          $t('CHATS_REPORTS.FILTERS.PLACEHOLDER.STATUS_AT_PERIOD_END')
        "
      />
      <ComboBox
        v-if="showChatsFilters"
        v-model="reopening"
        :options="reopeningOptions"
        :placeholder="$t('CHATS_REPORTS.FILTERS.PLACEHOLDER.REOPENING')"
      />
      <ComboBox
        v-if="showChatsFilters"
        v-model="transfers"
        :options="transfersOptions"
        :placeholder="$t('CHATS_REPORTS.FILTERS.PLACEHOLDER.TRANSFERS')"
      />
      <ComboBox
        v-if="showChatsFilters"
        v-model="humanResponse"
        :options="humanResponseOptions"
        :placeholder="$t('CHATS_REPORTS.FILTERS.PLACEHOLDER.HUMAN_RESPONSE')"
      />
    </div>
  </div>
</template>
